import React, {useContext} from "react";
import {Box, Card, Stack, Typography} from "@mui/material";
import {
  CONTEXT_ACTION_TYPE,
  CUSTOM_WORLD_REGISTER_STEPS,
  CustomWorldRegisterPayload,
  DataStoreContext,
  initialState
} from "../../contexts/OperatingEnvironmentContext";
import useMutation from "../../../../hooks/useMutation";
import Pjdb1ImputEx from "../../../../components/inputs/Pjdb1ImputEx";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import WithLabel from "../forms/WithLabel";
import Pjdb1FileSelect from "../../../../components/inputs/Pjdb1FileSelect";
import Pjdb1TextField from "../../../../components/inputs/Pjdb1TextField";
import {fileUploade, registerCustomWorld} from "../../../../utils/awsAmplifyUtil";
import Pjdb1CheckBox from "../../../../components/inputs/Pjdb1CheckBox";
import {convertUnixTimeSec, makeDateJST} from "../../../../utils/dateUtil";

export default function CustomWorldRegisterInfoInputStep() {
  const {state, dispatch} = useContext(DataStoreContext);
  const [fileErr, setFileErr] = React.useState({err: false, errMsg: ""});
  const {mutate, formRef, isLoading} = useMutation({
    enableConfirm: false,
    successMsg: 'カスタムワールドの登録完了',
    mutationFn: async () => {
      // 1. Upload files
      const fileUploadedRes = await fileUploade(
        state.customWorldRegister.inputs.customWorldConfigFiles.find(() => true) as File,
        {
          disablePrefix: true,
          basePath: `world/customworld/${convertUnixTimeSec(makeDateJST())}`
        },
      );
      if (!fileUploadedRes.isSuccess || fileUploadedRes.data === undefined) {
        return 'ファイルのアップロードに失敗しました。';
      }

      // 2. Register base world
      const res = await registerCustomWorld({
        body: {
          worldId: state.customWorldRegister.inputs.baseWorld?.attr["world-id"] ?? "",
          modelIds: state.customWorldRegister.inputs.workModels.map(item => item.attr["model-id"]),
          "s3-uri": fileUploadedRes.data,
          num: Number(state.customWorldRegister.inputs.generatedQuantity),
          optional: {
            block_num_max: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.blockNumMax),
            place_stack_num_random: state.customWorldRegister.inputs.generationOption.placeStackNumRandom,
            gap_size_y: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.gapSizeY),
            gap_size_x: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.gapSizeX),
            margin_min_x: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.marginMinX),
            margin_min_y: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.marginMinY),
            fill_rate_range_min: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.fillRateRangeMin),
            fill_rate_range_max: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.fillRateRangeMax),
            work_pos_misalign_max_rate_x: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.workPosMisalignMaxRateX),
            work_pos_misalign_max_rate_y: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.workPosMisalignMaxRateY),
            abnormal_orien_work_exist_max_rate: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.abnormalOrienWorkExistMaxRate),
            mixed_in_work_exist_max_rate: convertToNumberUndefined(state.customWorldRegister.inputs.generationOption.mixedInWorkExistMaxRate),
          }
        }
      });

      return res.isSuccess;
    },
    validate: () => {
      if (state.customWorldRegister.inputs.customWorldConfigFiles.length === 0) {
        setFileErr({err: true, errMsg: "ファイルを選択してください。"});
        return false;
      }
      return true;
    },
    onClear: () => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
        payload: initialState.customWorldRegister satisfies CustomWorldRegisterPayload
      });
      setFileErr({err: false, errMsg: ""});
    }
  });

  return (
    <Card sx={{p: 3, width: '100%'}}>
      <Stack gap={4}>
        <Box display='flex' flexWrap='wrap' gap={2} alignItems='flex-end' justifyContent='space-between'>
          <Typography variant="h6">カスタムワールド</Typography>
          <Pjdb1ImputEx/>
        </Box>

        <form ref={formRef}>
          <Stack gap={2}>
            <WithLabel label='カスタムワールド設定ファイル'>
              <Pjdb1FileSelect
                label='ファイル選択'
                name='file'
                value={state.customWorldRegister.inputs.customWorldConfigFiles}
                onChange={(_, newFiles) => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
                    payload: {
                      inputs: {
                        ...state.customWorldRegister.inputs,
                        customWorldConfigFiles: newFiles,
                      }
                    } satisfies CustomWorldRegisterPayload
                  });
                }}
                multiple={false}
                accept={{'application/zip': ['.zip']}}
                err={fileErr.err}
                errMsg={fileErr.errMsg}
                xs={8}
                toolTip='カスタムワールド生成用の設定ファイルのzipファイルをアップロードする。'
              />
            </WithLabel>
            <WithLabel label='生成個数'>
              <Pjdb1TextField
                label=''
                required
                name='generatedQuantity'
                inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
                xs={8}
                value={state.customWorldRegister.inputs.generatedQuantity ?? undefined}
                onChange={(event) => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
                    payload: {
                      inputs: {
                        ...state.customWorldRegister.inputs,
                        generatedQuantity: event.target.value,
                      }
                    } satisfies CustomWorldRegisterPayload
                  });

                }}
                toolTip={(<Box>半角自然数<br/>ランダム生成するカスタムワールドの生成個数</Box>)}
              />
            </WithLabel>
            <GenerationOptions/>
          </Stack>
        </form>

        <Box component="section" sx={{textAlign: "center"}}>
          <Pjdb1Button
            label="戻る"
            variant="contained"
            color="inherit"
            isLoading={isLoading}
            sx={{m: 2}}
            onClick={() => {
              dispatch({
                type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
                payload: {
                  ...state.customWorldRegister,
                  step: CUSTOM_WORLD_REGISTER_STEPS.SELECTION_STEP,
                } satisfies CustomWorldRegisterPayload
              });
            }}
          />
          <Pjdb1Button
            label="カスタムワールド生成開始"
            variant="contained"
            color="primary"
            isLoading={isLoading}
            sx={{m: 2}}
            onClick={() => {
              mutate().catch(console.error)
            }}
          />
        </Box>
      </Stack>
    </Card>
  );
}

function GenerationOptions() {
  const {state, dispatch} = useContext(DataStoreContext);
  return (
    <>
      <WithLabel label='段の中の区画数の最大値'>
        <Pjdb1TextField
          label=''
          name='blockNumMax'
          inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.blockNumMax}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    blockNumMax: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip={(<Box>段の中の区画数（＝配置する対象物種類数）の最大値を指定する。<br/>本値を超える区画数が指定された場合は、本値を上限に段を分割する。</Box>)}
        />
      </WithLabel>
      <WithLabel label='積み上げ個数'>
        <Pjdb1CheckBox
          label='ランダム'
          name='placeStackNumRandom'
          value={state.customWorldRegister.inputs.generationOption.placeStackNumRandom}
          onChange={() => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    placeStackNumRandom: !state.customWorldRegister.inputs.generationOption.placeStackNumRandom,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip={(<Box>積み上げ個数を、積み上げ可能個数の最大値までの間でランダムとするオプション。<br/>未指定時は、積み上げられるだけ積み上げる。</Box>)}
        />
      </WithLabel>
      <WithLabel label='Y軸の間隔'>
        <Pjdb1TextField
          label=''
          unit='m'
          name='gapSizeY'
          inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.gapSizeY}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    gapSizeY: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='カスタムワールドに配置する対象物について、横並びの対象物同士（X/Y軸上で隣接した対象物同士）の間隔を指定する。単位[m]'
        />
      </WithLabel>
      <WithLabel label='X軸の間隔'>
        <Pjdb1TextField
          label=''
          unit='m'
          name='gapSizeX'
          inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.gapSizeX}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    gapSizeX: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='カスタムワールドに配置する対象物について、横並びの対象物同士（X/Y軸上で隣接した対象物同士）の間隔を指定する。単位[m]'
        />
      </WithLabel>
      <WithLabel label='X軸の最小間隔'>
        <Pjdb1TextField
          label=''
          unit='m'
          name='marginMinX'
          inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.marginMinX}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    marginMinX: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='カスタムワールドに配置する対象物について、Y軸における対象物と領域の端と間の間隔の最小値を指定する。単位[m]。'
        />
      </WithLabel>
      <WithLabel label='Y軸の最小間隔'>
        <Pjdb1TextField
          label=''
          unit='m'
          name='marginMinY'
          inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.marginMinY}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    marginMinY: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='カスタムワールドに配置する対象物について、Y軸における対象物と領域の端と間の間隔の最小値を指定する。単位[m]。'
        />
      </WithLabel>
      <WithLabel label='充填率範囲最小値'>
        <Pjdb1TextField
          label=''
          name='fillRateRangeMin'
          inputProps={{pattern: "0(\\.[0-9]+)?|1(\\.[0])?"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.fillRateRangeMin}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    fillRateRangeMin: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='充填率範囲の最小値(0.0～1.0)を指定する。'
        />
      </WithLabel>
      <WithLabel label='充填率範囲最大値'>
        <Pjdb1TextField
          label=''
          name='fillRateRangeMax'
          inputProps={{pattern: "0(\\.[0-9]+)?|1(\\.[0])?"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.fillRateRangeMax}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    fillRateRangeMax: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='充填率範囲の最大値(0.0～1.0)を指定する。'
        />
      </WithLabel>
      <WithLabel label='対象物位置X方向最大ずれ幅率'>
        <Pjdb1TextField
          label=''
          name='workPosMisalignMaxRateX'
          inputProps={{pattern: "0(\\.[0-9]+)?|1(\\.[0])?"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.workPosMisalignMaxRateX}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    workPosMisalignMaxRateX: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip={(
            <Box>対象物位置X方向最大ずれ幅率。
              <br/>対象物の配置位置のずれ幅(X方向)の最大値を指定するための割合。
              <br/>値域は0.0～1.0。0.0の場合は全くずれなく配置し、1.0の場合はgap_size_x / 2を最大として等間隔位置からずれた位置に配置する。
            </Box>
          )}
        />
      </WithLabel>
      <WithLabel label='対象物位置Y方向最大ずれ幅率'>
        <Pjdb1TextField
          label=''
          name='workPosMisalignMaxRateY'
          inputProps={{pattern: "0(\\.[0-9]+)?|1(\\.[0])?"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.workPosMisalignMaxRateY}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    workPosMisalignMaxRateY: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip={(
            <Box>対象物位置X方向最大ずれ幅率。
              <br/>対象物の配置位のずれ幅(X方向)の最大値を指定するための割合。
              <br/>値域は0.0～1.0。0.0の場合は全くずれなく配置し、1.0の場合はgap_size_x / 2を最大として等間隔位置からずれた位置に配置する。
            </Box>
          )}
        />
      </WithLabel>
      <WithLabel label='区画内異常姿勢対象物存在確率'>
        <Pjdb1TextField
          label=''
          name='abnormalOrienWorkExistMaxRate'
          inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.abnormalOrienWorkExistMaxRate}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    abnormalOrienWorkExistMaxRate: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='区画内異常姿勢対象物存在確率。一つの区画内に、異常姿勢である対象物が存在する確率'
        />
      </WithLabel>
      <WithLabel label='区画内混入対象物存在確率'>
        <Pjdb1TextField
          label=''
          name='mixedInWorkExistMaxRate'
          inputProps={{pattern: "[0-9]*\\.?[0-9]*"}}
          xs={8}
          value={state.customWorldRegister.inputs.generationOption.mixedInWorkExistMaxRate}
          onChange={(event) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
              payload: {
                inputs: {
                  ...state.customWorldRegister.inputs,
                  generationOption: {
                    ...state.customWorldRegister.inputs.generationOption,
                    mixedInWorkExistMaxRate: event.target.value,
                  }
                }
              } satisfies CustomWorldRegisterPayload
            });
          }}
          toolTip='区画内混入対象物存在確率。一つの区画内に、別種の対象物が混入する確率。'
        />
      </WithLabel>
    </>
  )
}

function convertToNumberUndefined(value: string): number | undefined {
  return value === '' ? undefined : Number(value);
}
